import { Component, ChangeDetectionStrategy, signal, HostListener, OnInit, OnDestroy, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterOutlet, Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { MatSidenavModule } from '@angular/material/sidenav';
import { GlobalHeaderComponent } from '../../../../shared/components/layout/global-header/global-header';
import { GlobalFooterComponent } from '../../../../shared/components/layout/global-footer/global-footer';
import { GlobalSidebarComponent } from '../../../../shared/components/layout/global-sidebar/global-sidebar';
import { SidebarItem } from '../../../../shared/components/layout/global-sidebar/sidebar.model';
import { resolveMenuPermissionsFromRoute, routeChainHidesSidebar } from '../../../../shared/components/layout/global-sidebar/sidebar-utils';
import { BreadcrumbComponent } from '../../../../shared/components/breadcrumb/breadcrumb.component';
import { TranslatePipe } from '@shared/pipes/translate.pipe';
import { BreadcrumbService } from '../../../../core/services/breadcrumb.service';
import { filter, map, startWith, Subscription } from 'rxjs';

@Component({
  selector: 'app-advances-layout',
  standalone: true,
  imports: [
    GlobalHeaderComponent,
    GlobalFooterComponent,
    GlobalSidebarComponent,
    RouterOutlet,
    MatSidenavModule,
    BreadcrumbComponent,
    TranslatePipe,
  ],
  templateUrl: './advances-layout.component.html',
  styleUrl: './advances-layout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdvancesLayoutComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private routerSub?: Subscription;
  protected breadcrumbService = inject(BreadcrumbService);
  isMobile = signal(typeof window !== 'undefined' ? window.innerWidth <= 768 : false);

  hideSidebar = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(() => routeChainHidesSidebar(this.activatedRoute.snapshot)),
      startWith(routeChainHidesSidebar(this.activatedRoute.snapshot)),
    ),
    { requireSync: true },
  );

  menuConfig: SidebarItem[] = [
    { id: 'dash', label: 'advances.sidebar.dashboard', icon: 'grid_view', route: '/advances', exact: true },
    { id: 'applications', label: 'advances.sidebar.applications', icon: 'description', route: '/advances/applications' },
    { id: 'recovery', label: 'advances.sidebar.recovery', icon: 'payments', route: '/advances/recovery' },
    { id: 'vouchers', label: 'advances.sidebar.vouchers', icon: 'receipt_long', route: '/advances/vouchers' },
    { id: 'receipt-vouchers', label: 'advances.sidebar.receiptVouchers', icon: 'receipt', route: '/advances/receipt-vouchers' },
    { id: 'ledger', label: 'advances.sidebar.ledger', icon: 'menu_book', route: '/advances/ledger' },
    { id: 'exceptional', label: 'advances.sidebar.exceptional', icon: 'info_outline', route: '/advances/exceptional-events' },
    {
      id: 'masterSetup',
      label: 'advances.sidebar.masterSetup',
      icon: 'settings',
      children: [
        { id: 'advanceType', label: 'advances.sidebar.advanceType', route: '/advances/master-setup/advance-type' },
        { id: 'vehicle', label: 'advances.sidebar.vehicle', route: '/advances/master-setup/vehicle' }
      ]
    },
    { id: 'audit', label: 'advances.sidebar.audit', icon: 'bar_chart', route: '/advances/audit-log' },
    { id: 'reports', label: 'advances.sidebar.reports', icon: 'trending_up', route: '/advances/reports' }
  ];

  ngOnInit(): void {
    this.resolvePermissionsFromRouter();
    this.routerSub = this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      this.resolvePermissionsFromRouter();
      if (typeof document !== 'undefined') {
        const mainContent = document.querySelector('.main-content');
        if (mainContent) mainContent.scrollTop = 0;
        const pageContainer = document.querySelector('.page-container');
        if (pageContainer) pageContainer.scrollTop = 0;
      }
    });
  }

  private resolvePermissionsFromRouter(): void {
    const rootRoute = this.router.config.find(r => r.path === 'advances');
    this.menuConfig = resolveMenuPermissionsFromRoute(this.menuConfig, rootRoute, 'advances');
  }

  @HostListener('window:resize')
  onResize() {
    if (typeof window !== 'undefined') {
      this.isMobile.set(window.innerWidth <= 768);
      if (this.isMobile()) this.sidenavOpened.set(false);
      else this.sidenavOpened.set(true);
    }
  }
  sidenavOpened = signal(typeof window !== 'undefined' ? window.innerWidth > 768 : true);

  toggleSidenav(): void {
    this.sidenavOpened.update(v => !v);
  }

  ngOnDestroy(): void {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
  }
}
